import { useState, useEffect } from 'react'
import {
  DndContext,
  DragEndEvent,
  DragStartEvent,
  DragOverlay,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import {
  SortableContext,
  sortableKeyboardCoordinates,
  rectSortingStrategy,
} from '@dnd-kit/sortable'
import type { PromptItem, Category } from '../utils/types'
import { getCategories } from '../utils/categoryUtils'
import SortablePromptCard from './SortablePromptCard'

const PROMPTS_BATCH_SIZE = 40

interface PromptListProps {
  prompts: PromptItem[]
  onEdit: (id: string) => void
  onDelete: (id: string) => void
  onReorder: (activeId: string, overId: string) => void
  onToggleEnabled?: (id: string, enabled: boolean) => void
  onTogglePinned?: (id: string, pinned: boolean) => void
}

const PromptList = ({
  prompts,
  onEdit,
  onDelete,
  onReorder,
  onToggleEnabled,
  onTogglePinned,
}: PromptListProps) => {
  const [categoriesMap, setCategoriesMap] = useState<Record<string, Category>>({})
  const [copiedId, setCopiedId] = useState<string | null>(null)
  const [activeId, setActiveId] = useState<string | null>(null)
  const [visibleCount, setVisibleCount] = useState(PROMPTS_BATCH_SIZE)

  // 拖拽传感器配置
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  )

  // 加载分类信息
  useEffect(() => {
    const loadCategories = async () => {
      try {
        const categoriesList = await getCategories()
        
        // 创建分类映射表
        const map: Record<string, Category> = {}
        categoriesList.forEach(category => {
          map[category.id] = category
        })
        setCategoriesMap(map)
      } catch (err) {
        console.error('加载分类失败:', err)
      }
    }
    
    loadCategories()
  }, [])

  useEffect(() => {
    setVisibleCount(PROMPTS_BATCH_SIZE)
  }, [prompts])

  // 拖拽开始处理
  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(event.active.id as string)
  }

  // 拖拽结束处理
  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event
    
    if (active.id !== over?.id && over?.id) {
      onReorder(active.id as string, over.id as string)
    }
    
    setActiveId(null)
  }

  // 复制提示词内容的函数
  const handleCopy = async (content: string, id: string) => {
    try {
      await navigator.clipboard.writeText(content)
      setCopiedId(id)
      // 2秒后清除复制状态
      setTimeout(() => {
        setCopiedId(null)
      }, 2000)
    } catch (err) {
      console.error('复制失败:', err)
      // 可以在这里添加错误提示
    }
  }

  // if (allPromptsCount === 0) {
  //   return (
  //     <div className='text-center py-12 bg-gray-50 dark:bg-gray-800 rounded-lg border-2 border-dashed border-gray-300 dark:border-gray-600'>
  //       <svg
  //         className='mx-auto h-12 w-12 text-gray-400 dark:text-gray-500'
  //         fill='none'
  //         viewBox='0 0 24 24'
  //         stroke='currentColor'
  //         aria-hidden='true'
  //       >
  //         <path
  //           strokeLinecap='round'
  //           strokeLinejoin='round'
  //           strokeWidth={1.5}
  //           d='M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10'
  //         />
  //       </svg>
  //       <p className='mt-4 text-lg font-medium text-gray-700 dark:text-gray-300'>还没有添加提示词</p>
  //       <p className='mt-2 text-gray-500 dark:text-gray-400'>点击"新增提示词"按钮创建您的第一个提示词</p>
  //     </div>
  //   )
  // }

  // 如果过滤后没有提示词，返回空，让PromptManager统一处理无结果显示
  if (prompts.length === 0) {
    return null
  }

  const visiblePrompts = prompts.slice(0, visibleCount)
  const hasMorePrompts = visibleCount < prompts.length

  // 获取当前被拖拽的提示词
  const activePrompt = activeId ? prompts.find(prompt => prompt.id === activeId) : null

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
      <SortableContext 
        items={visiblePrompts.map(p => p.id)} 
        strategy={rectSortingStrategy}
      >
        <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
          {visiblePrompts.map((prompt) => {
            const category = categoriesMap[prompt.categoryId]
            
            return (
              <SortablePromptCard
                key={prompt.id}
                prompt={prompt}
                category={category}
                onEdit={onEdit}
                onDelete={onDelete}
                onToggleEnabled={onToggleEnabled}
                onTogglePinned={onTogglePinned}
                onCopy={handleCopy}
                copiedId={copiedId}
              />
            )
          })}
        </div>

        {hasMorePrompts && (
          <div className='mt-6 flex flex-col items-center gap-3 rounded-2xl border border-dashed border-gray-300 dark:border-gray-600 bg-white/70 dark:bg-gray-800/70 px-4 py-5'>
            <div className='text-sm text-gray-600 dark:text-gray-300'>
              已显示 {visiblePrompts.length} / {prompts.length} 条提示词
            </div>
            <button
              type='button'
              onClick={() => setVisibleCount(count => Math.min(count + PROMPTS_BATCH_SIZE, prompts.length))}
              className='inline-flex items-center px-5 py-2.5 rounded-xl bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all duration-200'
            >
              <svg className='w-4 h-4 mr-2' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                <path strokeLinecap='round' strokeLinejoin='round' strokeWidth='2' d='M12 5v14m7-7H5' />
              </svg>
              加载更多
            </button>
          </div>
        )}
      </SortableContext>

      {/* 拖拽覆盖层 */}
      <DragOverlay>
        {activePrompt ? (
          <SortablePromptCard
            prompt={activePrompt}
            category={categoriesMap[activePrompt.categoryId]}
            onEdit={onEdit}
            onDelete={onDelete}
            onToggleEnabled={() => {}}
            onTogglePinned={onTogglePinned}
            onCopy={handleCopy}
            copiedId={copiedId}
            isDragOverlay={true}
          />
        ) : null}
      </DragOverlay>
    </DndContext>
  )
}

export default PromptList
